import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";
import RedirectBackButton from "components/RedirectBackButton";
import RowElement from "components/RowElement";
import getUserDisplayName from "helpers/userHelper";
import useGlobalContext from "hooks/globalData";
import useTitle from "hooks/useTitle";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";
import { ActiveExamRequest } from "services/KnowfullyAdminClient";
import ExamAttemptsList from "./ExamAttemptsList";

const ExamResultsPage = () => {
  const { formatMessage: t } = useIntl();

  const {
    organizationUserService,
    userSkuService,
    userEvaluationService,
    userCourseUrl,
  } = useGlobalContext();

  const { userId, userSkuId } = useParams<{
    userId: string;
    userSkuId: string;
  }>();

  const userResult = useQuery(
    ["getOrganizationUserDetails", userId],
    () => organizationUserService.getOrganizationUserDetails(userId!),
    {
      enabled: !!userId,
    }
  );

  const user = userResult?.data;
  const userName = getUserDisplayName(user, t({ id: "user" }));
  useTitle(t({ id: "examAttemptsForTitle" }, { name: userName }));

  const userSkuResult = useQuery(
    ["getUserSku", userSkuId],
    () =>
      userSkuService.getUserSku(
        user?.id,
        user?.organizationId,
        userSkuId!
      ),
    { keepPreviousData: true, enabled: userResult.isFetched }
  );

  const userSku = userSkuResult?.data?.data!;

  const examResult = useQuery(
    ["activeExamForSku", user?.organizationId, userSku?.skuId, user?.id],
    () =>
      userEvaluationService.getActiveExamForSku(
        ActiveExamRequest.fromJS({
          userId: user?.id,
          skuId: userSku.skuId,
          organizationId: user?.organizationId,
        })
      ),
    {
      enabled: userResult.isFetched && userSkuResult.isFetched,
    }
  );

  const exam = examResult?.data?.data;

  const rows = [
    RowElement("nameLabel", exam?.name ?? ""),
    RowElement("descriptionLabel", exam?.description ?? ""),
  ].filter((r) => !!r.component);

  const title = t(
    { id: "examAttemptsFor" },
    {
      name: userName,
      x: (chunks: React.ReactNode) => <i>{chunks}</i>,
      examName: exam?.name,
    }
  );

  if (
    userResult.isFetching ||
    userSkuResult.isFetching ||
    examResult.isFetching
  ) {
    return <CircularProgress size={48} style={{ margin: "20px" }} />;
  }

  return (
    <>
      <RedirectBackButton
        redirectRoute={userCourseUrl(userId, userSkuId)}
        labelKey="backToUserCourse"
      />
      <h1>{title}</h1>
      <Box mb={4}>
        <TableContainer component={Paper}>
          <Table>
            <TableBody>
              {rows.map(({ title, component }, i) => (
                <TableRow key={i}>
                  <TableCell component="th" scope="row">
                    {title}
                  </TableCell>
                  <TableCell>{component}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box my={2} />
        <ExamAttemptsList />
      </Box>
    </>
  );
};

export default ExamResultsPage;
